import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useRequestTerminalCountChangeMutation } from "Api/Mutations/Merchants/useRequestTerminalCountChangeMutation";
import type { ApiProblemDetails } from "Infrastructure/Api/Api";
import { useState } from "react";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const RESOURCES = Resources.Components.Merchant.OrbitL.TerminalCountChange;

export type TerminalCountChange = {
  facilityPublicID: string;
  facilityName: string;
  currentCount: number;
  requestedCount: number;
  /** Undefined when the merchant has no SoftPOS product, so the request leaves SoftPOS alone. */
  currentSoftPosCount?: number;
  requestedSoftPosCount?: number;
};

type Props = {
  merchantPublicID: string;
  changes: Array<TerminalCountChange>;
  currentTotal: number;
  currentSoftPosTotal?: number;
  onClose: () => void;
  onRequested: () => void;
};

const hasSoftPosChange = (change: TerminalCountChange) =>
  change.requestedSoftPosCount !== undefined &&
  change.requestedSoftPosCount !== change.currentSoftPosCount;

export const TerminalCountChangeRequestModal = ({
  merchantPublicID,
  changes,
  currentTotal,
  currentSoftPosTotal,
  onClose,
  onRequested,
}: Props) => {
  const { t } = useResource();
  const [note, setNote] = useState("");

  const { mutate: requestChange, isPending } =
    useRequestTerminalCountChangeMutation({
      onSuccess: () => {
        toast.success(t(RESOURCES.RequestSuccess));
        onRequested();
      },
      onError: (error: unknown) => {
        const apiErrors = (error as { data?: ApiProblemDetails })?.data?.errors;
        const hasOpenRequest =
          apiErrors && "TerminalCountChangeRecord" in apiErrors;
        toast.error(
          t(
            hasOpenRequest
              ? RESOURCES.RequestConflictError
              : RESOURCES.RequestError,
          ),
        );
      },
    });

  const requestedTotal = changes.reduce(
    (total, change) => total + (change.requestedCount - change.currentCount),
    currentTotal,
  );
  const isSoftPosTotalVisible =
    currentSoftPosTotal !== undefined && changes.some(hasSoftPosChange);
  const requestedSoftPosTotal = changes.reduce(
    (total, change) =>
      total +
      ((change.requestedSoftPosCount ?? 0) - (change.currentSoftPosCount ?? 0)),
    currentSoftPosTotal ?? 0,
  );

  const handleConfirm = () => {
    requestChange({
      merchantPublicID,
      request: {
        facilities: changes.map((change) => ({
          merchantFacilityPublicID: change.facilityPublicID,
          requestedCount: change.requestedCount,
          requestedSoftPosCount: change.requestedSoftPosCount,
        })),
        note: note.trim() || null,
      },
    });
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t(RESOURCES.ModalTitle)}</DialogTitle>
      <DialogContent>
        <Stack gap={2}>
          <DialogContentText>{t(RESOURCES.ModalDescription)}</DialogContentText>

          <Stack gap={0.5}>
            <Typography variant="subtitle2" fontWeight={600}>
              {t(RESOURCES.ModalSummaryHeading)}
            </Typography>
            {changes.map((change) => (
              <Stack key={change.facilityPublicID} gap={0.25}>
                {change.requestedCount !== change.currentCount && (
                  <Typography variant="body2">
                    {t(RESOURCES.ModalFacilityChange, {
                      facility: change.facilityName,
                      from: change.currentCount,
                      to: change.requestedCount,
                    })}
                  </Typography>
                )}
                {hasSoftPosChange(change) && (
                  <Typography variant="body2">
                    {t(RESOURCES.ModalFacilitySoftPosChange, {
                      facility: change.facilityName,
                      from: change.currentSoftPosCount ?? 0,
                      to: change.requestedSoftPosCount ?? 0,
                    })}
                  </Typography>
                )}
              </Stack>
            ))}
            <Typography variant="body2" fontWeight={600}>
              {t(RESOURCES.ModalTotal, {
                from: currentTotal,
                to: requestedTotal,
              })}
            </Typography>
            {isSoftPosTotalVisible && (
              <Typography variant="body2" fontWeight={600}>
                {t(RESOURCES.ModalSoftPosTotal, {
                  from: currentSoftPosTotal,
                  to: requestedSoftPosTotal,
                })}
              </Typography>
            )}
          </Stack>

          <TextField
            label={t(RESOURCES.ModalNoteLabel)}
            placeholder={t(RESOURCES.ModalNotePlaceholder)}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            multiline
            minRows={2}
            slotProps={{ htmlInput: { maxLength: 1000 } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPending}>
          {t(RESOURCES.ModalCancel)}
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={isPending}
        >
          {t(RESOURCES.ModalConfirm)}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
